import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sprout } from "lucide-react";
import { api, type Model } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CollectionTabs, LibraryFooter, LibraryHeader, LoadingGrid, matchesSearch, ModelSections, SearchBox } from "../components/Library";

/** The embryology area: its own page of models, kept out of the anatomy library. */
export default function Embryology() {
  const { user } = useAuth();
  const [models, setModels] = useState<Model[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .listModels()
      .then(({ models }) => setModels(models.filter((m) => m.collection === "embryology")))
      .catch((e) => setError(e.message));
  }, []);

  const filtered = useMemo(() => models?.filter((m) => matchesSearch(m, search)) ?? null, [models, search]);

  return (
    <div className="min-h-screen bg-[#f7f8f5]">
      <LibraryHeader />
      <main className="mx-auto max-w-7xl px-5 pb-12 sm:px-8">
        <div className="flex flex-col justify-between gap-4 py-8 sm:flex-row sm:items-end sm:py-10">
          <div className="w-full sm:w-auto">
            <CollectionTabs />
            <h1 className="text-2xl font-semibold tracking-tight text-slate-800 sm:text-3xl">Embryology</h1>
            <p className="mt-2 text-sm text-slate-500">Follow human development in 3D, from the early embryo to the fetus in the uterus.</p>
          </div>
          <SearchBox value={search} onChange={setSearch} label="Search embryology models" />
        </div>
        {error && <div role="alert" className="card mb-6 border-red-200 bg-red-50 p-4 text-sm text-red-700">Couldn't load models: {error}</div>}
        {filtered && filtered.length > 0 && <ModelSections collection="embryology" models={filtered} />}
        {models === null && !error && <LoadingGrid />}
        {filtered?.length === 0 && <div className="card py-14 text-center"><Sprout size={28} className="mx-auto mb-4 text-brand-400" /><h3 className="font-semibold text-slate-800">{search ? "No matching models" : "Embryology is taking shape"}</h3><p className="mt-2 text-sm text-slate-500">{search ? "Try another stage or structure, or clear your search." : "Models will appear here when your teacher adds them."}</p>{search && <button onClick={() => setSearch("")} className="btn-secondary mt-5">Clear search</button>}{!search && user?.role === "admin" && <Link to="/admin" className="btn-primary mt-5">Add embryology models</Link>}</div>}
        <LibraryFooter />
      </main>
    </div>
  );
}
